/* eslint-disable no-undef */
import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { colorPicker, RecognitionTickIcon } from "../../utils/icons";

// --- Utilities ---
function hsvToRgb(h, s, v) {
  s /= 100;
  v /= 100;
  const k = (n) => (n + h / 60) % 6;
  const f = (n) => v - v * s * Math.max(0, Math.min(k(n), 4 - k(n), 1));
  return [Math.round(f(5) * 255), Math.round(f(3) * 255), Math.round(f(1) * 255)];
}

function rgbToHex(r, g, b) {
  const toHex = (x) => x.toString(16).padStart(2, "0");
  return "#" + toHex(r) + toHex(g) + toHex(b).toUpperCase();
}

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const num = parseInt(clean, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let h = 0,
    s = max === 0 ? 0 : d / max,
    v = max;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  return [h, Math.round(s * 100), Math.round(v * 100)];
}

function applyOpacity(rgb, opacity) {
  const a = opacity / 100;
  const blended = rgb.map((c) => Math.round(c * a + 255 * (1 - a)));
  return [blended, rgbToHex(...blended)];
}

class CustomColorPicker extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.state = {
      hue: 280,
      sat: 50,
      val: 80,
      opacity: 100,
      rgb: [151, 81, 242],
      dragging: false,
    };
  }

  componentDidMount() {
    this.drawCanvas();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.hue !== this.state.hue) this.drawCanvas();
  }

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.onDocMouseMove);
    document.removeEventListener("mouseup", this.onDocMouseUp);
  }

  drawCanvas() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { width: w, height: h } = canvas;
    ctx.fillStyle = `hsl(${this.state.hue}, 100%, 50%)`;
    ctx.fillRect(0, 0, w, h);
    const whiteGrad = ctx.createLinearGradient(0, 0, w, 0);
    whiteGrad.addColorStop(0, "#fff");
    whiteGrad.addColorStop(1, "transparent");
    ctx.fillStyle = whiteGrad;
    ctx.fillRect(0, 0, w, h);
    const blackGrad = ctx.createLinearGradient(0, 0, 0, h);
    blackGrad.addColorStop(0, "transparent");
    blackGrad.addColorStop(1, "#000");
    ctx.fillStyle = blackGrad;
    ctx.fillRect(0, 0, w, h);
  }

  handleCanvasInteraction = (e) => {
    const rect = this.canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    const sat = Math.round((x / rect.width) * 100);
    const val = Math.round((1 - y / rect.height) * 100);
    const rgb = hsvToRgb(this.state.hue, sat, val);
    this.setState({ sat, val, rgb });
  };

  onDocMouseMove = (e) => {
    if (this.state.dragging) this.handleCanvasInteraction(e);
  };

  onDocMouseUp = () => {
    this.setState({ dragging: false });
    document.removeEventListener("mousemove", this.onDocMouseMove);
  };

  activateEyeDropper = async () => {
    if (!window.EyeDropper) {
      alert("Your browser does not support the EyeDropper API");
      return;
    }
    const eyeDropper = new window.EyeDropper();
    try {
      const result = await eyeDropper.open();
      const hexValue = result.sRGBHex.toUpperCase();
      const rgb = hexToRgb(hexValue);
      const [h, s, v] = rgbToHsv(...rgb);
      this.setState({ hue: h, sat: s, val: v, rgb });
    } catch (e) {
      console.log("Eye dropper cancelled");
    }
  };

  render() {
    const { hue, sat, val, opacity, rgb } = this.state;
    const { onClose, onDone } = this.props;

    const [displayRgb, displayHex] = applyOpacity(rgb, opacity);

    const commonHandle = {
      width: 14,
      height: 14,
      marginTop: -3,
      backgroundColor: "#fff",
      border: "2px solid #fff",
      boxShadow: "0 0 2px rgba(0,0,0,0.3)",
      opacity: 1,
      cursor: "grab",
    };

    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Choose Custom Color</span>
            <span style={closeBtnStyle} onClick={onClose}>
              ✕
            </span>
          </div>

          <div style={instructionStyle}>Choose a color using the color picker</div>

          <div className='divBorder' style={{border:'0.75px solid #efefef' ,padding:'8px',borderRadius:'4px',background:'#F8F8F8',marginBottom:'20px'}}>
            {/* Color Area */}
            <div
              style={canvasContainerStyle}
              onMouseDown={(e) => {
                this.setState({ dragging: true });
                this.handleCanvasInteraction(e);
                document.addEventListener("mousemove", this.onDocMouseMove);
                document.addEventListener("mouseup", this.onDocMouseUp, { once: true });
              }}
            >
              <canvas ref={this.canvasRef} width={280} height={140} style={{ width: "100%", height: "100%" }} />
              <div style={{ ...cursorStyle, left: `${sat}%`, top: `${100 - val}%` }} />
            </div>

            {/* Eye Dropper & Sliders Area */}
            <div style={controlsLayout}>
              <div onClick={this.activateEyeDropper} style={eyeDropperBtn}>
                {colorPicker()}
              </div>

              <div style={{ flex: 1 }}>
                <div style={sliderRow}>
                  <Slider
                    min={0}
                    max={360}
                    value={hue}
                    onChange={(h) => {
                      const newRgb = hsvToRgb(h, sat, val);
                      this.setState({ hue: h, rgb: newRgb });
                    }}
                    railStyle={hueRailStyle}
                    trackStyle={{ background: "transparent" }}
                    handleStyle={commonHandle}
                  />
                </div>
                <div style={sliderRow}>
                  <Slider
                    min={0}
                    max={100}
                    value={opacity}
                    onChange={(o) => this.setState({ opacity: o })}
                    railStyle={{
                      height: 8,
                      borderRadius: 4,
                      background: `linear-gradient(to right, rgba(${rgb[0]},${rgb[1]},${rgb[2]},0), rgb(${rgb[0]},${rgb[1]},${rgb[2]}))`,
                    }}
                    trackStyle={{ background: "transparent" }}
                    handleStyle={commonHandle}
                  />
                </div>
              </div>
            </div>

            {/* HEX / RGB Values */}
            <div style={valuesRow}>
              <div style={{ flex: 3.2 }}>
                <div style={valLabel}>HEX</div>
                <input readOnly value={displayHex} style={valInput} />
              </div>
              {["R", "G", "B"].map((l, i) => (
                <div key={l} style={{ flex: 1 }}>
                  <div style={valLabel}>{l}</div>
                  <input readOnly value={displayRgb[i]} style={valInput} />
                </div>
              ))}
            </div>
          </div>

          {/* Done Button */}
          <button onClick={() => onDone(displayHex)} style={btnStyle}>
            Done <span style={{ marginLeft: 8 }}>{RecognitionTickIcon("#fff")}</span>
          </button>
        </div>
      </div>
    );
  }
}

// --- Styles ---
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 };
const modalStyle = { background: "#fff", borderRadius: 12, width: 400, padding: "16px", boxShadow: "0 8px 30px rgba(0,0,0,0.2)", fontFamily: "'Lato', sans-serif" };
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, borderBottom: "1px solid #efefef", paddingBottom: "20px" };
const titleStyle = { fontSize: 18, fontWeight: 700, color: "#184A61" };
const closeBtnStyle = { cursor: "pointer", color: "#DA6C74", fontSize: 18, fontWeight: "bold" };
const instructionStyle = { fontSize: 16, color: "#184A61", marginBottom: 20 };
const canvasContainerStyle = { position: "relative", height: 150, borderRadius: 8, overflow: "hidden", cursor: "crosshair", marginBottom: 8 };
const cursorStyle = { position: "absolute", width: 12, height: 12, borderRadius: "50%", border: "2px solid white", transform: "translate(-50%, -50%)", pointerEvents: "none", boxShadow: "0 0 2px rgba(0,0,0,0.5)" };

const controlsLayout = { display: "flex", alignItems: "center", gap: 12, marginBottom: 20 };
const eyeDropperBtn = { padding: 8, border: "1px solid #E9ECEF", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: "#C96BAE" };
const sliderRow = { height: 28, display: "flex", alignItems: "center", overflow: "visible" };
const hueRailStyle = { height: 8, borderRadius: 4, background: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" };

const valuesRow = { display: "flex", gap: 10 };
const valLabel = { fontSize: 12, color: "#184A61", marginBottom: 4};
const valInput = { width: "100%", padding: "8px 4px", border: "1px solid #E9ECEF", borderRadius: 4, fontSize: 12, color: "#184A61", textAlign: "center", background: "#F8F9FA" };
const btnStyle = { width: "96px", padding: "4px", background: "#DA6C74", color: "#fff", border: "none", borderRadius: 8, fontWeight: 500, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", margin: "24px auto 0", height: "40px" };

CustomColorPicker.propTypes = { onDone: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired };

export default CustomColorPicker;